import { ThemeOptions } from "@mui/material";

import { IdsExpandMore as ArrowDropDownIcon } from "components/shared/svgIcons";

import { colors, palette } from "./palette";

const textPrimaryOnLight = colors.nero[300];

export const components: ThemeOptions["components"] = {
  MuiAccordion: {
    styleOverrides: {
      root: {
        boxShadow: "none",
        borderRadius: 4,
        "&:before": {
          display: "none",
        },
        "&.Mui-expanded:first-of-type": {
          marginTop: 16,
        },
      },
    },
  },
  MuiAccordionDetails: {
    styleOverrides: {
      root: {
        padding: "0 24px 24px 24px",
      },
    },
  },
  MuiAccordionSummary: {
    styleOverrides: {
      root: {
        padding: "0 24px",
      },
      content: {
        margin: "16px 0",
      },
    },
  },
  MuiBreadcrumbs: {
    styleOverrides: {
      separator: {
        marginLeft: 0,
        marginRight: 0,
      },
    },
  },
  MuiButton: {
    defaultProps: {
      variant: "contained",
      color: "primary",
      disableRipple: true,
      disableElevation: true,
    },
    styleOverrides: {
      root: {
        fontWeight: 600,
        padding: "8px 24px",
        fontSize: 14,
        letterSpacing: 0.5,
        lineHeight: "24px",
        textAlign: "center",
        textTransform: "initial",

        "&:focus": {
          outline: `2px solid ${textPrimaryOnLight}`,
          outlineOffset: 2,
        },
      },
      contained: {
        "&.MuiButton-containedTertiary:focus": {
          outlineColor: colors.white,
        },
      },
      sizeLarge: {
        padding: "12px 32px",
        fontSize: 14,
      },
      containedSizeLarge: {
        padding: "12px 32px",
      },
      textSizeLarge: {
        padding: "12px 30px",
      },
      sizeSmall: {
        fontSize: 14,
        padding: "4px 18px",
      },
      containedSizeSmall: {
        padding: "4px 18px",
      },
      textSizeSmall: {
        padding: "4px 16px",
      },
      outlined: {
        borderWidth: "2px !important",
        padding: "6px 22px",

        "&:hover": {
          color: "#FFF",
        },
        "&.Mui-disabled": {
          color: colors.grey[300],
        },
      },
      outlinedSizeLarge: {
        height: 48,
      },
      outlinedSizeSmall: {
        height: 32,
      },
      outlinedPrimary: {
        borderColor: palette.primary.main,

        "&:hover": {
          backgroundColor: `${palette.primary.dark} !important`,
          borderColor: palette.primary.dark,
        },
      },
      outlinedSecondary: {
        borderColor: palette.secondary.main,

        "&:hover": {
          backgroundColor: palette.secondary.main,
        },
      },
      // Below: use variant="text" to get ghost button styling
      text: {
        "--main-hover-bgcolor": palette.grey[500],
        color: textPrimaryOnLight,
        backgroundColor: "transparent",
        padding: "8px 24px",
        "&:hover, &:focus": {
          backgroundColor: "var(--main-hover-bgcolor)",
        },
        "&.MuiButton-textPrimary:hover, &.MuiButton-textPrimary:focus": {
          color: palette.primary.dark,
        },
        "&.MuiButton-textSecondary:hover, &.MuiButton-textSecondary:focus": {
          color: palette.secondary.dark,
        },
        "&.Mui-disabled": { color: palette.grey[300], cursor: "not-allowed" },
      },
      textSecondary: {
        color: colors.periwinkle[500],
      },
    },
  },
  MuiCard: {
    defaultProps: {
      elevation: 0,
    },
    styleOverrides: {
      root: {
        background: colors.periwinkle[100],
      },
    },
  },
  MuiCardActionArea: {
    styleOverrides: {
      root: {
        "&.Mui-focusVisible": {
          backgroundColor: colors.periwinkle[100],
        },
      },
    },
  },
  MuiCardContent: {
    styleOverrides: {
      root: {
        padding: "24px 24px 32px",
        "&:last-child": {
          paddingBottom: 32,
        },
      },
    },
  },
  MuiCheckbox: {
    styleOverrides: {
      root: {
        "&.Mui-disabled": {
          color: `${colors.grey[300]} !important`,
        },
      },
    },
  },
  MuiChip: {
    styleOverrides: {
      root: {
        color: textPrimaryOnLight,
        backgroundColor: palette.common.white,
        fontSize: 12,
        fontWeight: 600,
        letterSpacing: 0.5,
        border: `1px solid ${colors.grey[600]}`,
        "&.error": {
          "& .MuiChip-deleteIcon": {
            color: palette.error.main,
          },
        },
        "&.Mui-disabled": {
          backgroundColor: palette.grey[600],
          color: colors.nero[200],
          opacity: 1,
        },
      },
      label: {
        overflowWrap: "break-word",
        whiteSpace: "normal",
        textOverflow: "clip",
      },
      deleteIcon: {
        color: palette.primary.main,
        "&:hover": { color: palette.primary.dark },
        "&:disabled": { color: colors.semantic.neutral },
      },
      clickable: {
        "&:hover": {
          color: palette.primary.dark,
          backgroundColor: palette.grey[600],
          cursor: "default",
        },
      },
      deletable: {
        "&:focus": {
          color: palette.primary.dark,
          backgroundColor: palette.grey[600],
          outline: "auto",
        },
      },
    },
  },
  MuiContainer: {
    defaultProps: {
      maxWidth: "xl",
    },
    styleOverrides: {
      root: ({ theme }) => ({
        [theme.breakpoints.up("xs")]: {
          paddingLeft: 32,
          paddingRight: 32,
        },
        [theme.breakpoints.up("md")]: {
          paddingLeft: 48,
          paddingRight: 48,
        },
        [theme.breakpoints.up("lg")]: {
          paddingLeft: 64,
          paddingRight: 64,
        },
        [theme.breakpoints.up("xl")]: {
          paddingLeft: 80,
          paddingRight: 80,
        },
      }),
      maxWidthLg: {
        padding: "0 80px",
      },
    },
  },
  MuiDivider: {
    styleOverrides: {
      root: {
        borderColor: colors.grey[500],
      },
    },
  },
  MuiFilledInput: {
    styleOverrides: {
      root: {
        backgroundColor: colors.grey[600],
        "&.Mui-focused": {
          backgroundColor: colors.grey[600],
        },
        "&::before": {
          borderBottom: "none",
        },
        "& > .MuiFilledInput-input": {
          padding: "21px 16px 6px",
          fontSize: 14,
          lineHeight: 1.5,
          letterSpacing: 0.5,
          color: textPrimaryOnLight,
        },
        "&:hover": {
          backgroundColor: `${colors.grey[600]} !important`,
          "&:before": {
            borderBottom: "none !important",
          },
        },
        "&$disabled": {
          backgroundColor: "transparent !important",
        },
        "&$error": {
          "& .MuiSelect-icon": { color: colors.semantic.error },
        },
        "&.MuiInputBase-readOnly": {
          backgroundColor: "transparent !important",
          pointerEvents: "none",
          "&:after": {
            borderBottom: "none !important",
          },
        },
      },
      underline: {
        "&.Mui-error:after": {
          transform: "scaleX(1)",
        },
      },
    },
  },
  MuiFormControl: {
    defaultProps: {
      variant: "standard",
    },
  },
  MuiFormHelperText: {
    styleOverrides: {
      root: {
        color: palette.text.secondary,
      },
    },
  },
  MuiGrid: {
    styleOverrides: {
      root: {},
    },
  },
  MuiIconButton: {
    styleOverrides: {
      colorInherit: {
        color: textPrimaryOnLight,
      },
      sizeSmall: {
        padding: 8,
      },
      colorSecondary: {
        "&:hover": {
          backgroundColor: colors.grey[500],
        },
      },
    },
  },
  MuiInputBase: {
    styleOverrides: {
      input: {
        "&::placeholder": {
          color: colors.grey[100],
        },
      },
    },
  },
  MuiLink: {
    defaultProps: {
      underline: "none",
    },
    styleOverrides: {
      root: {
        cursor: "pointer",
        fontFamily: "'Futura Allele', sans-serif",
        "&[disabled]": {
          color: "grey",
          cursor: "default",
        },
        textUnderlineOffset: "4px",
      },
      underlineNone: ({ ownerState }) => ({
        ...(ownerState.color === "secondary" && {
          "&:hover": {
            color: `${palette.secondary.dark} !important`,
          },
        }),
        ...(ownerState.color === "primary" && {
          "&:hover": {
            color: `${palette.primary.dark} !important`,
          },
        }),
      }),
      underlineAlways: ({ ownerState }) => ({
        ...(ownerState.color === "secondary" && {
          "&:hover": {
            color: `${palette.secondary.dark} !important`,
          },
        }),
        ...(ownerState.color === "primary" && {
          "&:hover": {
            color: `${palette.primary.dark} !important`,
          },
        }),
      }),
    },
  },
  MuiListItem: {
    styleOverrides: {
      divider: {
        borderBottomColor: colors.grey[500],
      },
    },
  },
  MuiMenuItem: {
    styleOverrides: {
      root: {
        "&:hover": {
          backgroundColor: colors.grey[500],
        },
      },
    },
  },
  MuiOutlinedInput: {
    styleOverrides: {
      root: {
        background: "#FFF",
        borderRadius: 0,
        "& svg": {
          color: colors.grey[300],
        },
      },
      input: {
        padding: "11px 16px",
      },
      notchedOutline: { borderColor: colors.grey[400] },
    },
  },
  MuiPaper: {
    styleOverrides: {
      root: {
        color: textPrimaryOnLight,
      },
    },
  },
  MuiSelect: {
    defaultProps: {
      variant: "standard",
      IconComponent: ArrowDropDownIcon,
      MenuProps: {
        anchorOrigin: {
          vertical: "top",
          horizontal: "left",
        },
        transformOrigin: {
          vertical: "top",
          horizontal: "left",
        },
      },
    },
    styleOverrides: {
      icon: {
        color: palette.primary.main,
        width: "1.125em",
        height: "1.125em",
        top: "calc(50% - 14px)",
        right: 7,
      },
    },
  },
  MuiTableCell: {
    styleOverrides: {
      body: {
        color: textPrimaryOnLight,
      },
      head: {
        color: textPrimaryOnLight,
      },
      root: {
        fontSize: 16,
        lineHeight: "23px",
        borderColor: colors.tableBorderColor,
        borderBottomColor: colors.tableBorderColor,
      },
      sizeSmall: {
        padding: "12px 24px 11px 16px",
      },
    },
  },
  MuiTableRow: {
    styleOverrides: {
      root: {
        backgroundColor: palette.background.paper,
        "&$hover:hover": { backgroundColor: colors.catSkill },
      },
    },
  },
  MuiTab: {
    styleOverrides: {
      textColorInherit: { color: "inherit" },
      textColorPrimary: { color: colors.grey[100] },
      textColorSecondary: { color: colors.grey[100] },
      root: {
        "&$selected": {
          '&[class*="textColorInherit"]': { colors: "#000" },
          '&[class*="textColorPrimary"]': { colors: palette.primary.main },
          '&[class*="textColorSecondary"]': { colors: palette.secondary.main },
        },
      },
    },
  },
  MuiTextField: {
    defaultProps: {
      variant: "standard",
    },
    styleOverrides: {
      root: {
        "& > .MuiInputLabel-filled": {
          fontSize: 12,
          lineHeight: 1.5,
          letterSpacing: 0.5,
          color: colors.grey[100],
          transform: "translate(16px, 15px)",
          "&.MuiInputLabel-shrink": {
            transform: "translate(16px, 1px)",
          },
          "&.Mui-error": {
            color: colors.semantic.error,
          },
          "&.Mui-focused": {
            color: palette.primary.main,
          },
          "&.Mui-disabled": {
            color: colors.grey[300],
          },
        },
        "& .MuiFormHelperText-root": {
          "&.Mui-focused": {
            fontStyle: "italic",
            color: colors.ocean[600],
          },
          "&.Mui-error": {
            fontSize: 12,
            lineHeight: 1.5,
            letterSpacing: 0.5,
            fontStyle: "italic",
            color: colors.semantic.error,
          },
        },
      },
    },
  },
  MuiTypography: {
    styleOverrides: {
      root: {
        "& abbr[title], &abbr[title]": {
          textDecoration: "none",
          boxShadow: `0px -1px 0px  ${colors.grey[600]} inset`,
          "&:hover, &:focus": {
            color: palette.primary.main,
            boxShadow: `0px -1px 0px  ${palette.primary.light} inset`,
          },
          "&::selection": {
            color: palette.common.white,
            backgroundColor: palette.primary.main,
          },
        },
        "&.sr-only": {
          width: 1,
          height: 1,
          overflow: "hidden",
          position: "absolute",
        },
      },
    },
  },
};
